import type { IBinderListings, IBinderRegistry, IContainer } from '../interface';
import type { IBinder } from '../interface/binder';
import { isBinder } from './binder';
import { buildBinderListings, isBinderListings } from './binderListings';
import { buildObjectContainer } from './container';

const _findBindable = (listings: IBinderListings[], key: symbol): IBinder<unknown> | undefined => {
    for (const listing of listings) {
        const item = listing.bindersForSymbol(key);

        if (item) {
            return item;
        }
    }

    return undefined;
};

const _indexOfListing = (listings: IBinderListings[], id: symbol): number => {
    const item = listings.find(itemSearch => itemSearch.id === id);
    return item ? listings.indexOf(item) : -1;
};

async function _resolve<T>(
    listings: IBinderListings[],
    container: IContainer,
    key: symbol,
): Promise<T | undefined> {
    const binder = _findBindable(listings, key);

    if (binder) {
        await Promise.all(
            binder.dependencies.map(dependencyKey => _resolve(listings, container, dependencyKey)),
        );
        await binder.bind(container);
        const value = await binder.inject(container);
        return value as T;
    }

    return undefined;
}

export function isBinderRegistry(object: unknown): object is IBinderRegistry {
    const isNullOrUndefined = (value: unknown): boolean => value === null || value === undefined;

    if (isNullOrUndefined(object) || typeof object !== 'object') {
        return false;
    }

    const record: Partial<IBinderRegistry> = object as Partial<IBinderRegistry>;

    return (
        !isNullOrUndefined(record.exists) &&
        !isNullOrUndefined(record.get) &&
        !isNullOrUndefined(record.addListing) &&
        !isNullOrUndefined(record.removeListing) &&
        !isNullOrUndefined(record.listings) &&
        !isNullOrUndefined(record.addListings) &&
        !isNullOrUndefined(record.removeListings) &&
        !isNullOrUndefined(record.addBindable) &&
        !isNullOrUndefined(record.removeBindable) &&
        !isNullOrUndefined(record.container) &&
        !isNullOrUndefined(record.resolve) &&
        !isNullOrUndefined(record.resolveAll)
    );
}

export function buildBinderRegistry(parents: IBinderRegistry[] = []): Readonly<IBinderRegistry> {
    const parentContainers = parents.map(parent => parent.container());
    const container = buildObjectContainer(parentContainers);

    const publicListings: IBinderListings[] = [];
    const privateListings: IBinderListings[] = [];
    const _parents: IBinderRegistry[] = parents;

    const binderRegistry: IBinderRegistry = {
        exists: (key: symbol) => _findBindable(publicListings, key) !== undefined,

        async get<T>(this: IBinderRegistry, key: symbol) {
            const isPrivate = _findBindable(privateListings, key) !== undefined;

            if (isPrivate) {
                return undefined;
            }

            if (container.exists(key)) {
                return container.get(key);
            }

            for (const parent of _parents) {
                if (!parent.exists(key)) {
                    continue;
                }

                const result = await parent.get<T>(key).catch(() => undefined);

                if (result) {
                    return result;
                }
            }

            /* Don't allow explicit dereferencing of symbols declared as private, however expose 
               it to #_resolve as it may be needed as part of the dependency graph to resolve */
            const result = await _resolve<T>(
                [...publicListings, ...privateListings],
                container,
                key,
            );

            if (!result) {
                throw new Error(`Unable to resolve symbol:${String(key)}`);
            }

            return result;
        },
        addListing: (listing: IBinderListings, hidden?: boolean): void => {
            const list = hidden ?? false ? privateListings : publicListings;
            list.push(listing);
        },
        removeListing: (listing: IBinderListings): void => {
            publicListings.slice(publicListings.indexOf(listing), 1);
            privateListings.slice(publicListings.indexOf(listing), 1);
        },
        addListings: (listings: Readonly<Readonly<IBinderListings>[]>, hidden?: boolean): void => {
            const list = hidden ?? false ? privateListings : publicListings;
            list.push(...listings);
        },
        removeListings: (listings: Readonly<Readonly<IBinderListings>[]>): void => {
            listings.forEach(listing => publicListings.slice(publicListings.indexOf(listing), 1));
            listings.forEach(listing => privateListings.slice(privateListings.indexOf(listing), 1));
        },
        listings: () => publicListings,
        addBindable: (binder: IBinder<unknown>, hidden?: boolean) => {
            const list = hidden ?? false ? privateListings : publicListings;
            list.push(buildBinderListings([binder], binder.id));
        },
        removeBindable: (binder: IBinder<unknown>) => {
            privateListings.splice(_indexOfListing(privateListings, binder.id), 1);
            publicListings.splice(_indexOfListing(publicListings, binder.id), 1);
        },
        container: () => container,
        async resolve(
            this: IBinderRegistry,
            items: Readonly<(IBinder<unknown> | IBinderListings | symbol)[]>,
        ): Promise<void> {
            const allKeys = items.flatMap(item => {
                if (typeof item === 'symbol') {
                    return [item];
                } else if (isBinder(item)) {
                    return [item.id];
                } else if (isBinderListings(item)) {
                    return item.allKeys();
                }
                return [undefined];
            });

            const resolvers = allKeys
                .filter(item => item !== undefined)
                .map(item => (item ? this.get(item) : Promise.resolve()));

            await Promise.all(resolvers);
        },
        async resolveAll(this: IBinderRegistry) {
            const allKeys = [...privateListings, ...publicListings].flatMap(listing =>
                listing.allKeys(),
            );

            for (const key of allKeys) {
                await this.get(key);
            }
        },
    };

    return Object.freeze(binderRegistry);
}
